import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAppColor } from "@icarius-app/selectors";
import { IconButton, Typography, Paper, Grow, InputAdornment, MenuItem, Chip } from "@material-ui/core";
import IcariusIcon from "@icarius-assets/images/icariusIcon.png";
import { CloseIcon, SendIcon } from "@icarius-icons";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CancelIcon from "@material-ui/icons/Cancel";
import Draggable from "react-draggable";
import { formatDraftToText } from "@icarius-utils/format";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useBotDraggable from "@icarius-common/botAssistant/components/useBotDraggable";
import ProfileImg from "@icarius-common/profileImg";
import Message from "@icarius-common/botAssistant/components/message";
import CustomTextField from "@icarius-common/botAssistant/components/customTextField";
import DotsMenu from "@icarius-common/dotsMenu";
import { AddIcon } from "@icarius-icons/index";
import { formatDateHHMM } from "@icarius-utils/date";

const MAX_FILES = 4;
const MAX_SIZE_MB = 15; // 15 mb como máximo
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv",
];

const createMessage = (role, content) => ({
    role,
    content,
    time: formatDateHHMM(new Date()),
});

const ChatBox = (props) => {
    const { open, isLoading, grid, queryPreset, geminiContext, questions, handleSendMessage, handleClose } = props;

    const defaultMessageState = useMemo(() => {
        return [
            createMessage("gemini", { type: "text", format: "plain", text: getLocalizedString("botInitialMessage") }),
        ];
    }, []);

    const dispatch = useDispatch();
    const [messages, setMessages] = useState(defaultMessageState);
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const color = useSelector(getAppColor);

    const { dragRef, bounds } = useBotDraggable(open);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
            if (inputRef.current) inputRef.current.focus();
        }
    }, [open, files, messages]);

    useEffect(() => {
        setMessages(defaultMessageState);
    }, [queryPreset, defaultMessageState]);

    const buildRequestData = useCallback(
        (prompt, files) => {
            const data = {};
            data.prompt = prompt;
            data.files = files;
            data.id = queryPreset;

            if (!geminiContext) {
                let rowList = [];
                grid.api.forEachNodeAfterFilterAndSort((node) => rowList.push(node.data));

                const headerData = grid.columnApi.getAllDisplayedColumns().map((node) => {
                    return {
                        field: node.colDef.field,
                        headerName: node.colDef.headerName,
                        cellRenderer: node.colDef.cellRenderer,
                    };
                });

                // no es modo pivote
                let fileContent = headerData.map((item) => item.headerName).join(";") + "\r\n"; // armar encabezado con los nombres de los campos

                rowList.forEach((row) => {
                    headerData.forEach((headerElement) => {
                        let field = headerElement.field;

                        if (row?.hasOwnProperty(field)) {
                            if (row[field] === null) {
                                fileContent += ";";
                            } else {
                                if (headerElement.cellRenderer === "RichTextRenderer") {
                                    fileContent += formatDraftToText(JSON.parse(row[field])) + ";";
                                } else {
                                    fileContent += row[field] + ";";
                                }
                            }
                        }
                    });

                    if (row) {
                        // si se agregó contenido, poner el salto de línea
                        fileContent = fileContent.substring(0, fileContent.length - 1); // sacarle el ultimo ;
                        fileContent += "\r\n";
                    }
                });

                data.file = new Blob([fileContent], { type: "text/csv" });
                data.type = "query";
            } else {
                data.conversationId = geminiContext.conversationId;
            }

            return data;
        },
        [grid, geminiContext, queryPreset]
    );

    const sendMessage = async (prompt, attachments) => {
        if (!prompt.trim() && attachments.length === 0) return;

        setText("");
        setFiles([]);
        setMessages((prev) => [
            ...prev,
            createMessage("user", { type: "text", format: "plain", text: prompt, attachments }),
        ]);

        const requestResponse = await handleSendMessage(buildRequestData(prompt, attachments));
        const { status, response } = requestResponse?.data || {};
        if (status !== "OK" || !response) return;

        setMessages((prev) => [...prev, createMessage("gemini", response)]);
    };

    const submit = (e) => {
        e.preventDefault();
        sendMessage(text, files);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            submit(e);
        }
    };

    const handleFileSelect = (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const selectedFiles = Array.from(e.target.files);
        let validFiles = [];

        if (files.length + selectedFiles.length > MAX_FILES) {
            dispatch(
                openSnackbarAction({
                    msg: getLocalizedString("maxFilesGemini").replace("{maxFiles}", MAX_FILES),
                    severity: "error",
                    duration: 10000,
                })
            );
            selectedFiles.length = Math.max(0, MAX_FILES - files.length);
        }

        selectedFiles.forEach((file) => {
            if (!ALLOWED_TYPES.includes(file.type)) {
                dispatch(
                    openSnackbarAction({
                        msg: getLocalizedString("invalidFileTypeGemini"),
                        severity: "error",
                        duration: 10000,
                    })
                );
                return;
            }

            if (file.size > MAX_SIZE_BYTES) {
                dispatch(
                    openSnackbarAction({
                        msg: getLocalizedString("invalidSizeGemini").replace("{maxSize}", MAX_SIZE_MB),
                        severity: "error",
                        duration: 10000,
                    })
                );
                return;
            }

            validFiles.push(file);
        });

        if (validFiles.length > 0) {
            setFiles((prev) => [...prev, ...validFiles]);
        }

        e.target.value = null;
        if (inputRef.current) inputRef.current.focus();
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    return (
        <>
            {open && (
                <Draggable nodeRef={dragRef} handle=".bot-drag-handle" cancel="button" bounds={bounds}>
                    <div
                        ref={dragRef}
                        style={{
                            position: "fixed",
                            right: 10,
                            bottom: 10,
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 10,
                        }}
                    >
                        <Grow in={open} style={{ transformOrigin: "bottom right" }} unmountOnExit>
                            <Paper
                                role="dialog"
                                style={{
                                    borderRadius: 10,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "calc(100vh - 125px)",
                                    maxHeight: 700,
                                    width: 450,
                                    maxWidth: "calc(100vw - 20px)",
                                    overflow: "hidden",
                                    background: "transparent",
                                }}
                            >
                                <header
                                    className="bot-drag-handle"
                                    style={{
                                        background: color,
                                        padding: 10,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexShrink: 0,
                                        cursor: "move",
                                        userSelect: "none",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <ProfileImg image={IcariusIcon} size={40} external />
                                        <div>
                                            <Typography style={{ fontWeight: 600, color: "white" }}>
                                                {getLocalizedString("icariusAssistant")}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: "white" }}>
                                                {getLocalizedString("howCanIHelp")}
                                            </Typography>
                                        </div>
                                    </div>
                                    <IconButton onClick={handleClose}>
                                        <CloseIcon style={{ fill: "white" }} />
                                    </IconButton>
                                </header>
                                <section
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        background: "var(--mainBackgroundColor)",
                                        border: "1px solid var(--secondaryBackgroundColor)",
                                        borderRadius: "0px 0px 10px 10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            padding: 10,
                                            overflowY: "auto",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 10,
                                        }}
                                    >
                                        {messages.map((message, index) => {
                                            return (
                                                <Message
                                                    key={index}
                                                    text={message.content.text}
                                                    status={message.content.type}
                                                    attachments={message.content.attachments}
                                                    time={message.time}
                                                    isUser={message.role === "user"}
                                                />
                                            );
                                        })}
                                        {isLoading && <Message isUser={false} />}
                                        <div ref={scrollRef} />
                                    </div>
                                    <footer
                                        style={{
                                            flexShrink: 0,
                                            padding: 10,
                                            paddingBottom: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        {Boolean(questions?.length) && !Boolean(files?.length) && !isLoading && (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 5,
                                                    overflow: "auto",
                                                }}
                                            >
                                                <DotsMenu
                                                    icon={
                                                        <Chip
                                                            avatar={
                                                                <AddIcon
                                                                    style={{ background: "none", borderRadius: "50%" }}
                                                                />
                                                            }
                                                            label={getLocalizedString("suggestedQuestions")}
                                                            color="primary"
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    }
                                                    anchorOrigin={{
                                                        vertical: "top",
                                                        horizontal: "center",
                                                    }}
                                                    transformOrigin={{
                                                        vertical: "bottom",
                                                        horizontal: "center",
                                                    }}
                                                    noPadding
                                                >
                                                    {questions
                                                        .sort((a, b) => a.order - b.order)
                                                        .map(({ question }, questionIndex) => {
                                                            return (
                                                                <MenuItem
                                                                    key={questionIndex}
                                                                    onClick={() => sendMessage(question)}
                                                                    style={{ textWrap: "wrap", fontSize: 14 }}
                                                                >
                                                                    {question}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                </DotsMenu>
                                            </div>
                                        )}
                                        {Boolean(files?.length) && (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 5,
                                                    overflow: "auto",
                                                }}
                                            >
                                                {files.map((file, fileIndex) => {
                                                    return (
                                                        <Chip
                                                            key={`${file.name}-${file.lastModified}`}
                                                            label={file.name}
                                                            onDelete={() => handleRemoveFile(fileIndex)}
                                                            deleteIcon={
                                                                <CancelIcon style={{ fill: "var(--mainText)" }} />
                                                            }
                                                            size="small"
                                                            style={{
                                                                background: "var(--tertiaryBackgroundColor)",
                                                                border: "1px solid var(--borderColor)",
                                                            }}
                                                            className="whiteText"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <form onSubmit={submit} style={{ width: "100%" }} noValidate>
                                            <CustomTextField
                                                inputRef={inputRef}
                                                disabled={isLoading}
                                                size={"small"}
                                                placeholder={getLocalizedString("writeYourMessage")}
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                multiline={true}
                                                rowsMax={5}
                                                fullWidth={true}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment
                                                            position="end"
                                                            style={{ display: "flex", gap: 5 }}
                                                        >
                                                            <IconButton
                                                                title={getLocalizedString("attachFile")}
                                                                edge="end"
                                                                onClick={() => fileInputRef.current.click()}
                                                                disabled={isLoading}
                                                            >
                                                                <AttachFileIcon style={{ fill: "var(--mainText)" }} />
                                                            </IconButton>
                                                            <IconButton
                                                                title={getLocalizedString("sendMessage")}
                                                                edge="end"
                                                                type="submit"
                                                                disabled={isLoading}
                                                            >
                                                                <SendIcon style={{ fill: "var(--mainText)" }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileSelect}
                                                style={{ display: "none" }}
                                                accept="image/jpeg, image/png, image/webp, application/pdf, .xls, .xlsx, .txt, .csv"
                                                multiple
                                            />
                                        </form>
                                        <Typography variant="caption" className="whiteText">
                                            {getLocalizedString("doNotShareInformation")}
                                        </Typography>
                                    </footer>
                                </section>
                            </Paper>
                        </Grow>
                    </div>
                </Draggable>
            )}
        </>
    );
};

export default ChatBox;
